/**
 * Quiz API client
 */

import { fetchWithAuth } from './core';

export interface QuizRun {
  id: string;
  score_pct: number | null;
  domain_scores: Record<string, number>;
  weak_domains: string[];
  passed: boolean | null;
  time_taken_s: number;
  started_at?: string;
  completed_at?: string;
}

export interface QuizRunSubmitResult {
  run_id: string;
  mode?: 'exam' | 'survey';
  score_pct: number | null;
  domain_scores: Record<string, number>;
  weak_domains: string[];
  passed: boolean | null;
  time_taken_s: number;
  completed_at: string;
}

export interface QuizRunHistory {
  runs: QuizRun[];
  best_score_pct: number;
  runs_count: number;
  trend: number;
}

export interface QuizReviewItem {
  id: string;
  domain: string;
  stem: string;
  options: Record<string, string>;
  user_answer: string | null;
  correct_answer: string;
  is_correct: boolean;
  explanation?: string;
}

export interface QuizRunDetail {
  run_id: string;
  score_pct: number;
  domain_scores: Record<string, number>;
  weak_domains: string[];
  passed: boolean;
  time_taken_s: number;
  started_at?: string;
  completed_at?: string;
  document_id?: string;
  passing_score: number;
  selection?: QuizSelection | null;
  items: QuizReviewItem[];
  wrong_only?: boolean;
  items_count?: number;
}

export type QuizSelection = {
  count?: number;
  domains?: string[];
  question_ids: string[];
  mode?: string;
  verified_only?: boolean;
  exclude_ids?: string[];
};

export async function submitQuizRun(
  slug: string,
  payload: {
    answers: Record<string, string>;
    started_at: string;
    completed_at: string;
    selection?: QuizSelection | null;
  }
): Promise<QuizRunSubmitResult> {
  const res = await fetchWithAuth(`/api/quiz/${encodeURIComponent(slug)}/runs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Submit failed (${res.status})`);
  }
  return res.json();
}

export async function sampleQuizQuestions(
  slug: string,
  payload: {
    count?: number;
    domains?: string[];
    verified_only?: boolean;
    exclude_ids?: string[];
    exclude_mock_slug?: string;
    domain_weights?: Record<string, number>;
  }
): Promise<{
  document_id: string;
  bank_slug: string;
  questions: import('@/lib/quiz-parser').QuizQuestion[];
  selection: QuizSelection;
  available: number;
}> {
  const res = await fetchWithAuth(`/api/quiz/${encodeURIComponent(slug)}/sample`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Sample failed (${res.status})`);
  }
  return res.json();
}

export async function fetchQuizRuns(slug: string, limit = 10): Promise<QuizRunHistory> {
  const res = await fetchWithAuth(
    `/api/quiz/${encodeURIComponent(slug)}/runs?limit=${limit}`
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Fetch runs failed (${res.status})`);
  }
  return res.json();
}

export async function fetchLatestQuizRun(slug: string): Promise<QuizRun | null> {
  const res = await fetchWithAuth(
    `/api/quiz/${encodeURIComponent(slug)}/runs/latest`
  );
  if (res.status === 404) return null;
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Fetch latest run failed (${res.status})`);
  }
  return res.json();
}

export async function fetchQuizRunDetail(
  slug: string,
  runId: string,
  opts?: { wrongOnly?: boolean }
): Promise<QuizRunDetail> {
  const params = new URLSearchParams();
  if (opts?.wrongOnly) params.set('wrong_only', 'true');
  const qs = params.toString();
  const res = await fetchWithAuth(
    `/api/quiz/${encodeURIComponent(slug)}/runs/${encodeURIComponent(runId)}${qs ? `?${qs}` : ''}`
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Fetch run detail failed (${res.status})`);
  }
  return res.json();
}

export interface QuizPracticeDetail {
  kind: 'practice';
  session_id: string;
  source: string;
  correct_count: number;
  total: number;
  domain_scores: Record<string, number>;
  weak_domains: string[];
  started_at?: string;
  completed_at?: string;
  document_id?: string;
  slug?: string;
  items: QuizReviewItem[];
  wrong_only?: boolean;
  items_count?: number;
}

export async function fetchQuizPracticeDetail(
  slug: string,
  sessionId?: string | null,
  opts?: { wrongOnly?: boolean }
): Promise<QuizPracticeDetail> {
  const params = new URLSearchParams();
  if (opts?.wrongOnly) params.set('wrong_only', 'true');
  const qs = params.toString();
  const path = sessionId
    ? `/api/quiz/${encodeURIComponent(slug)}/practice/sessions/${encodeURIComponent(sessionId)}`
    : `/api/quiz/${encodeURIComponent(slug)}/practice/latest`;
  const res = await fetchWithAuth(`${path}${qs ? `?${qs}` : ''}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Fetch practice detail failed (${res.status})`);
  }
  return res.json();
}

export interface QuizPracticeSubmitResult {
  session_id: string;
  correct_count: number;
  total: number;
  domain_scores: Record<string, number>;
  weak_domains: string[];
  completed_at: string;
  document_id?: string;
}

export interface QuizPracticeSummary {
  document_id: string;
  slug: string;
  sessions_count: number;
  sessions: Array<{
    id: string;
    source: string;
    correct_count: number;
    total: number;
    score_pct?: number | null;
    domain_scores: Record<string, number>;
    weak_domains: string[];
    wrong_question_ids?: string[];
    wrong_stems?: string[];
    completed_at?: string;
  }>;
  aggregate: {
    correct_count: number;
    total: number;
    accuracy_pct: number | null;
    frequent_weak_domains: Array<{ domain: string; sessions: number }>;
  };
}

export async function submitQuizPracticeSession(
  slug: string,
  payload: {
    answers: Record<string, { choice: string; correct: boolean; ms?: number } | string>;
    source?: 'document_tab' | 'chat';
    started_at: string;
    completed_at: string;
    selection?: QuizSelection | null;
  }
): Promise<QuizPracticeSubmitResult> {
  const res = await fetchWithAuth(`/api/quiz/${encodeURIComponent(slug)}/practice`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Practice submit failed (${res.status})`);
  }
  return res.json();
}

export async function fetchQuizPracticeSummary(
  slug: string,
  limit = 20
): Promise<QuizPracticeSummary> {
  const res = await fetchWithAuth(
    `/api/quiz/${encodeURIComponent(slug)}/practice/summary?limit=${limit}`
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Practice summary failed (${res.status})`);
  }
  return res.json();
}
